import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Alert } from "@/components/ui/alert";
import { ExportLink } from "@/components/ui/data-table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { InschrijfperiodeKiezer } from "@/features/inschrijfperiodes/InschrijfperiodeKiezer";
import { vensterStatus } from "@/features/inschrijfperiodes/venster";
import type { Inschrijfperiode } from "@/features/inschrijfperiodes/useInschrijfperiodes";
import { NieuwePloeg } from "@/features/inschrijving/NieuwePloeg";
import { PloegenTabel, PloegenTabelSkeleton } from "@/features/inschrijving/PloegenTabel";
import { WijzigPloeg } from "@/features/inschrijving/WijzigPloeg";
import {
  useInschrijving,
  useKorpsPeriodes,
  type Inschrijving,
} from "@/features/inschrijving/useInschrijving";
import { exportHref, type components } from "@jbnl/api-client";

type KorpsOption = components["schemas"]["KorpsOption"];

/**
 * One route set serves both doors: namens handelen is a property of the grant, not the door, so
 * a beheerder and the korps produce identical audit rows (ADR-0004, ADR-0022).
 */
export function PloegenDetail({
  seizoenId,
  korps,
  gekozenPeriodeId,
  onKiesPeriode,
}: {
  seizoenId: string;
  korps: KorpsOption;
  /** Resolved and held by `PloegenPage` (#198): the korpsenlijst's filter needs it too. */
  gekozenPeriodeId: string | undefined;
  onKiesPeriode: (id: string) => void;
}) {
  const { t } = useTranslation("ploegen");
  const { data: periodes } = useKorpsPeriodes(seizoenId);

  // Which ploeg the modal is showing - see the Aanmeldsite's twin for why this is state and
  // not a search param.
  const [gekozenPloegId, setGekozenPloegId] = useState<string>();

  const periode = periodes?.find((p) => p.id === gekozenPeriodeId);
  const venster = periode ? vensterStatus(periode) : undefined;

  const { data: inschrijving, isPending, isError } = useInschrijving(periode?.id, korps.id);

  return (
    <div className="space-y-4">
      {/* The korps name titles the card rather than heading it, so the pane reads as a record
        and not a fragment of the page around it. */}
      <Card>
        <CardHeader>
          <CardTitle>{korps.name}</CardTitle>
          <CardDescription>{korps.city}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Visible while it happens: the audit row records both, which is what matters when a
            korps disputes a submission. */}
          <Alert tone="info">
            <Trans
              t={t}
              i18nKey="detail.namens"
              values={{ korps: korps.name }}
              components={{ strong: <strong /> }}
            />
          </Alert>

          <AanmeldBlok
            periodes={periodes}
            gekozenPeriodeId={periode?.id}
            venster={venster}
            korps={korps}
            inschrijving={inschrijving}
            onKies={onKiesPeriode}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("lijst.label")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <PloegenLijstBlok
            periode={periode}
            isPending={isPending}
            isError={isError}
            inschrijving={inschrijving}
            onKiesPloeg={setGekozenPloegId}
          />
        </CardContent>
      </Card>

      {/* Outside both cards: a modal is not part of either surface, it covers them. */}
      {inschrijving && (
        <WijzigPloeg
          inschrijving={inschrijving}
          ploeg={inschrijving.ploegen.find((p) => p.ploegId === gekozenPloegId)}
          korpsId={korps.id}
          gesloten={false}
          onSluiten={() => setGekozenPloegId(undefined)}
        />
      )}
    </div>
  );
}

function AanmeldBlok({
  periodes,
  gekozenPeriodeId,
  venster,
  korps,
  inschrijving,
  onKies,
}: {
  periodes: Inschrijfperiode[] | undefined;
  gekozenPeriodeId: string | undefined;
  venster: ReturnType<typeof vensterStatus> | undefined;
  korps: KorpsOption;
  inschrijving: Inschrijving | undefined;
  onKies: (inschrijfperiodeId: string) => void;
}) {
  const { t } = useTranslation("ploegen");

  return (
    <>
      {periodes && periodes.length > 0 ? (
        <div className="grid justify-items-start gap-3">
          {/* Above the button, not in a toolbar: the chosen periode decides what the button
            enters a ploeg into. */}
          <InschrijfperiodeKiezer
            id="ploeg-periode"
            className="min-w-64"
            periodes={periodes}
            gekozen={gekozenPeriodeId}
            onKies={onKies}
          />

          {inschrijving && (
            // The secondary act left of the primary one, as on the planning screen.
            <div className="flex flex-wrap items-center justify-end gap-2 justify-self-stretch">
              <ExportLink
                href={exportHref(
                  "/api/inschrijfperiodes/{inschrijfperiodeId}/korpsen/{korpsId}/inschrijving/export",
                  { path: { inschrijfperiodeId: inschrijving.inschrijfperiodeId, korpsId: korps.id } },
                )}
              />
              <NieuwePloeg
                inschrijving={inschrijving}
                korpsId={korps.id}
                korpsName={korps.name}
                gesloten={false}
              />
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{t("detail.geenPeriode")}</p>
      )}

      {venster && !venster.open && (
        <p className="text-sm text-muted-foreground">
          {t("detail.vensterOverrule", { venster: venster.label, korps: korps.name })}
        </p>
      )}
    </>
  );
}

function PloegenLijstBlok({
  periode,
  isPending,
  isError,
  inschrijving,
  onKiesPloeg,
}: {
  periode: Inschrijfperiode | undefined;
  isPending: boolean;
  isError: boolean;
  inschrijving: Inschrijving | undefined;
  onKiesPloeg: (ploegId: string) => void;
}) {
  const { t } = useTranslation("ploegen");

  return (
    <>
      {periode && isPending && <PloegenTabelSkeleton />}

      {isError && <p className="text-sm text-destructive">{t("errors.ophalen")}</p>}

      {inschrijving && (
        <>
          <PloegenTabel inschrijving={inschrijving} onKies={onKiesPloeg} />

          {inschrijving.ploegen.length > 0 && (
            <p className="text-sm text-muted-foreground">{t("lijst.kiesPloeg")}</p>
          )}
        </>
      )}
    </>
  );
}
